import {
  collection,
  addDoc,
  getDoc,
  doc,
  onSnapshot,
  query,
  where,
  setDoc,
  limit,
  orderBy,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { EmploymentFormState } from './types';
import { IBudgetFormData } from './components/Forms/BudgetRequestForm';
import { IBreastFormData } from './components/Forms/BreastCancer';
import { useEffect, useState } from 'react';
import {
  getStorage,
  ref,
  uploadString,
  getDownloadURL,
  uploadBytesResumable,
} from 'firebase/storage';
import { db } from './firebase';

export const FormIds = {
  contact: 'contact',
  volunteer: 'volunteer',
  schoolBash2025: 'school_bash_2023',
  budgetForm: 'budget-request-form',
  // breastCancerSeminar2023: 'breast-cancer-seminar-form-2023',
  // breastCancerSeminar2024: 'breast-cancer-seminar',
  breastCancerSeminar: 'breast-cancer-seminar',
  mentalHealth2024: 'mental-health-symposium-2024',
  skateworldSpringBreak: 'skate_night',
  bolden2024: 'bolden_2024',
  schoolBash2024: 'school_bash_2024',
  requisitionForm: 'requisition-form',
  youthChristmasPart2024: 'youth-christmas-party-2024',
  breastCancerSeminar2025: 'breastCancerSeminar2025',
};
export interface Ministry {
  id: string;
  name: string;
  description: string;
  media: string[];
  content?: string;
  leaders?: string | null;
  passage?: string | null;
}

export interface MinistryPost {
  id: string;
  ministryId: string;
  authorName: string;
  title: string;
  content: string;
  imageUrl?: string;
  linkUrl?: string;
  linkLabel?: string;
  createdAt: Date;
}

export interface Announcement {
  id: string;
  text: string;
  link?: string;
  callToAction?: string;
  createdOn: Date;
  displayUntil: Date;
  creatorId: string;
  enabled: boolean;
}

export interface ChurchEvent {
  id: string;
  title: string;
  description: string;
  startTime: Date;
  endTime: Date;
  media: string[];
  creator: string;
  signUpFormId?: string;
  link: string;
  externalLink?: string;
  orderText?: string;
}

export interface VolunteerInput {
  lastName: string;
  firstName: string;
  middleName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  homePhone: string;
  cellPhone: string;
  email: string;
  ministry: string;
  acceptedJesus: boolean;
  explanation: string;
  churchHistory: string;
  completedClass: boolean;
  volunteerExperience: string;
  backgroundCheck: boolean;
  employment?: EmploymentFormState | null;
  signature?: string;
  signatureDate?: string;
}

// ...existing code...

export const useMinistries = () => {
  const [busy, setBusy] = useState(false);
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  useEffect(() => {
    setMinistries([]);
    const q = query(collection(db, 'ministries'), orderBy('name', 'asc'));
    setBusy(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setBusy(false);
      const z: Ministry[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        z.push({
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          media: data.media || (data.imageUrl ? [data.imageUrl] : []),
          content: data.content || '',
          leaders: Array.isArray(data.leaders)
            ? data.leaders.map((l: { name: string }) => l.name).join(', ')
            : data.leaders || null,
          passage: data.passage || null,
        } as Ministry);
      });
      setMinistries(z);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return { busy, ministries };
};

export const useMinistryPosts = (ministryId: string) => {
  const [posts, setPosts] = useState<MinistryPost[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ministryId) return;
    setPosts([]);
    const postsRef = collection(db, 'ministries', ministryId, 'posts');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    setBusy(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setBusy(false);
      const z: MinistryPost[] = [];
      querySnapshot.forEach((d) => {
        const data = d.data();
        z.push({
          id: d.id,
          ministryId,
          authorName: data.authorName || '',
          title: data.title || '',
          content: data.content || '',
          imageUrl: data.imageUrl,
          linkUrl: data.linkUrl,
          linkLabel: data.linkLabel,
          createdAt: data.createdAt?.toDate?.() || new Date(),
        });
      });
      setPosts(z);
    });
    return () => {
      unsubscribe();
    };
  }, [ministryId]);

  return { busy, posts };
};

// Legacy hardcoded ministries kept as fallback reference
export const churchMinistries: Ministry[] = [
  {
    id: 'marriage',
    name: 'MARRIAGE MINISTRY',
    description:
      "The Marriage Ministry's mission is designed to strengthen, support, and celebrate the covenant of marriage. Participants meet on a regular basis as a group, to discuss how to apply biblical concepts to nurture their marriages. Sessions are open to married and engaged couples, as well as individuals interested in marriage.",
    content:
      "<p>The Marriage Ministry's mission is designed to strengthen, support, and celebrate the covenant of marriage. Participants meet on a regular basis as a group, to discuss how to apply biblical concepts to nurture our marriages. Sessions are open to married and engaged couples, as well as individuals interested in marriage.</p>",
    leaders: 'Bro Ruky Ojameruaye, Sis Chase Ojameruaye',
    media: [
      'https://nmzlakeland.org/static/media/marriage.86de854cbd76aea3eb49.jpg',
    ],
  },
  {
    id: 'evangelism',
    name: 'EVANGELISM MINISTRY',
    passage: `Therefore make disciples of all nations, baptizing them in the name of the
Father and of the Son and of the Holy Spirit, 20 and teaching them to obey
everything I have commanded you. And surely, I am with you always, to
the very end of the age.” - Mathew 28:19-20 NIV`,
    description:
      'The Evangelism Ministry is the heartbeat of outreach within the church—where the message of hope, grace, and salvation is shared with intention, compassion, and conviction. It is about connection, meeting people where they are, building relationships, and inviting them into a transformative journey with Christ.',
    content:
      "<p>The Evangelism Ministry is the heartbeat of outreach within the church—where the message of hope, grace, and salvation is shared with intention, compassion, and conviction. It's not just about proclamation; it's about connection. Meeting people where they are, building relationships, and inviting them into a transformative journey with Christ.</p>",
    leaders: null,
    media: [
      'https://nmzlakeland.org/static/media/evangelism.842e49fcee1b7df3a183.jpg',
    ],
  },
  {
    id: 'trustee',
    name: 'TRUSTEE MINISTRY',
    description:
      "The Trustee Ministry is called to a solemn sense of responsibility, representing and acting in the overall best interest of the church. They serve as asset management and financial accountability overseers, faithfully stewarding all the resources of God's Church. They provide oversight and control of all the church's buildings and grounds, and are responsible for the church's financial operations, including budget development and financial records maintenance.",
    content: `<p>
    The New Mt. Zion Missionary Baptist Church’s Trustee Ministry is called to a
solemn sense of responsibility. This ministry represents and acts in the overall
best interest of the church and ensures that effective and efficient operations are
developed, evaluated, and maintained.
    </p>
    <p>
    The Trustee Ministry serves as the church’s asset management and financial
accountability overseers and acts as faithful stewards of all the resources of
God’s Church. They provide oversight and control of all the church’s buildings
and grounds and are responsible for policies and procedures that govern the use
of church buildings and equipment. The Trustee Ministry is responsible for the
church’s financial operations, including budget development, accounts manage-
ment, counting and deposits, asset management, and maintenance of financial
records.
    </p>
    `,
    leaders: 'Sister Barbara Harrison',
    media: [
      'https://nmzlakeland.org/static/media/trustee.6a98d36dd554198d6e9d.jpg',
    ],
  },
  {
    id: 'deacons',
    name: 'DEACONS MINISTRY',
    passage: 'Acts 6:1-6 and 1 Timothy 3:8-13.',
    description:
      'The Deacons Ministry is a cornerstone of servant leadership, rooted in the biblical call to care for the body of Christ. They are spiritual shepherds, trusted stewards, and bridge-builders between the pulpit and the pews.',
    content:
      '<p>The Deacons Ministry is a cornerstone of servant leadership within the church—quietly powerful, deeply compassionate, and rooted in the biblical call to care for the body of Christ. Deacons are not just helpers; they are spiritual shepherds, trusted stewards, and bridge-builders between the pulpit and the pews.</p>',
    leaders: 'Deacon Samuel Simmons',
    media: [
      'https://nmzlakeland.org/static/media/deacons.8ce8c86612d9b6df0b0c.jpg',
    ],
  },
  {
    id: 'hospitality',
    name: 'HOSPITALITY MINISTRY',
    description:
      "The Hospitality Ministry is the heart and handshake of the church, creating an atmosphere where warmth meets welcome and every detail whispers, 'You belong here.' It's a ministry of presence, preparation, and genuine care, ensuring members and guests feel seen, valued, and embraced.",
    content:
      '<p>The Hospitality Ministry is the heart and handshake of the church—where warmth meets welcome, and every detail whispers, "You belong here." It\'s a ministry of presence, preparation, and genuine care, creating an atmosphere where members and guests alike feel seen, valued, and embraced.</p>',
    leaders: 'Sis. Judy Fields',
    media: [
      'https://nmzlakeland.org/static/media/hosp.489e0996fbe19cbcdffc.jpg',
    ],
  },
  {
    id: 'deaconess',
    name: 'DEACONESS MINISTRY',
    passage: `In the same way, their wives are to be women worthy of respect, not malicious
talkers, but temperate and trustworthy in everything. - 1 Timothy 3:11 NIV`,
    description:
      'The Deaconess Ministry is a sacred embodiment of grace in action—women called to serve with compassion, dignity, and spiritual strength. They are pillars of care, prayer, and presence, ministering to the heart of the church with a nurturing spirit and unwavering faith.',
    content:
      '<p>The Deaconess Ministry is a sacred embodiment of grace in action—women called to serve with compassion, dignity, and spiritual strength. Often working quietly behind the scenes, deaconesses are pillars of care, prayer, and presence, ministering to the heart of the church with a nurturing spirit and unwavering faith.</p>',
    leaders: 'Sis Geanie Moore',
    media: [
      'https://nmzlakeland.org/static/media/deaconess.9818f6d39a03ae3d1fe9.jpg',
    ],
  },
  {
    id: 'decor',
    name: 'DECOR MINISTRY',
    description:
      'The Décor Ministry volunteers use their talents to create and enhance a beautiful, welcoming, and worshipful environment that reflects Christian love, glorifies God, and reflects His beauty. They provide a visual expression of worship that inspires, comforts, and uplifts, celebrating the seasons of faith with beauty and intentionality.',
    content:
      "<p>The Décor Ministry volunteers use their talents and gifts to create and enhance a beautiful, welcoming, and worshipful environment that reflects Christian love, glorifies God and reflects His beauty. They provide a visual expression of worship that transforms sacred spaces into environments that also inspire, comfort, and uplift. It's more than arranging flowers or hanging banners; it's about setting the spiritual tone, honoring tradition, and celebrating the seasons of faith with beauty and intentionality.</p>",
    leaders: 'Sis Keitha Salmon, Sister Brenda Shaw',
    media: [
      'https://nmzlakeland.org/static/media/decor.3996f897b2744f170b2c.jpg',
    ],
  },
  {
    id: 'prison',
    name: 'PRISON MINISTRY',
    passage: `“………………. I was in prison, and you came to visit me.”
Matthew 25:36 NIV`,
    description:
      "The Prison Ministry serves those men, women, and children affected by crime and incarceration, working to see lives restored in and out of prison through fellowship and sharing God's plan for salvation. This ministry visits jails and prisons to evangelize, provide hope, and proclaim the word of God.",
    content:
      "<p>The Prison Ministry believes that every person is made in the image of God and that no life is beyond His reach. This ministry serves those men, women, and children affected by crime and incarceration and works to see lives restored in and out of prison through fellowship and sharing God's plan for salvation. This ministry visits jails and prisons to evangelize, provide hope, and proclaim the word of God and His saving power to inmates and others, letting them know that God can change their lives and remove the guilt and shame from their past behaviors.</p>",
    leaders: 'Sis. Virginia Scarlett',
    media: [
      'https://nmzlakeland.org/static/media/prison.fb43f4ed949570cf64b8.jpg',
    ],
  },
  {
    id: 'shepherds',
    name: "SHEPHERD'S CARE MINISTRY",
    description:
      "The Shepherd's Care Ministry is a sacred support system, anointed to uplift, encourage, and care for the shepherd of the house (the pastor). It is a ministry of honor and service, where practical help meets spiritual intercession, ensuring the pastor can lead with strength, clarity, and peace.",
    content:
      "<p>The Shepherd's Care Ministry is a sacred support system—anointed to uplift, encourage, and care for the shepherd of the house. It's a ministry of honor and service, where practical help meets spiritual intercession, ensuring that the pastor can lead with strength, clarity, and peace.</p>",
    leaders: 'Sis. Lynne Simpkins',
    media: [
      'https://nmzlakeland.org/static/media/shepherds.676430eb2957b6e29c39.jpg',
    ],
  },
  {
    id: 'ushers',
    name: 'USHERS MINISTRY',
    passage: `“Better is one day in your courts than a thousand elsewhere; I would
rather be a doorkeeper in the house of my God than dwell in the
tents of the wicked.” – Psalm 84:10 NIV`,
    description:
      "The Ushers Ministry serves as both doorkeepers and spiritual ambassadors, extending the love of Jesus Christ through warm greetings and gracious gestures. They welcome and seat members and guests, maintain order, respond to the congregation's needs, and distribute materials.",
    content:
      "<p>The Ushers Ministry serves as both doorkeepers and spiritual ambassadors, extending the love of Jesus Christ through every warm greeting and gracious gesture. As visible representatives of Christ, ushers welcome and seat members and guests, creating a comfortable and reverent atmosphere for worship. With attentive hearts and watchful eyes, they maintain order at the sanctuary entrance, respond to the needs of the congregation, and distribute bulletins and materials that highlight church programs and upcoming events. Their presence reflects hospitality, care, and a commitment to serving God's people with dignity and grace.</p>",
    leaders:
      'Adult Ushers: Sis Verdean Brown. Youth Ushers: Deacon Brian Austin, Sister Doris Henley, Andrea Williams.',
    media: [
      'https://nmzlakeland.org/static/media/ushers.71919840ef900ead4778.jpg',
    ],
  },
  {
    id: 'sunday_school',
    name: 'SUNDAY SCHOOL MINISTRY',
    passage: `“For whatever was written in the former days was written for our instruction,
that through endurance and through the encouragement of the Scriptures,
we might have hope. Romans 15:4`,
    description:
      "Sunday School is the heart of the church's teaching ministry—a sacred space where faith is nurtured, Scripture is explored, and lives are shaped across generations. Rooted in the call to 'gather the people... that they may hear and learn,' it reaches, teaches, wins, and cares for every soul entrusted to them.",
    content: `
    <p>
    Sunday School is the heart of our church’s teaching ministry—a sa-
cred space where faith is nurtured, Scripture is explored, and lives
are shaped across generations. Rooted in the call to “gather the
people… that they may hear and learn” (Deuteronomy 31:12), our
Sunday School 
    </p>
    <p>CHILDREN’S SUNDAY SCHOOL</p>
    <p>“Train up a child in the way he should go, and when he is old, he will not depart
from it. Proverbs 22:6
Children’s Sunday School age groups are five to eight years of age
and nine to twelve years of age.</p>
    `,
    leaders: 'Deacon Sam Simmons',
    media: [
      'https://nmzlakeland.org/static/media/sundayschool.c17d630620687e9db37c.jpg',
    ],
  },

  {
    id: 'ministers_ministry',
    name: "MINISTERS' MINISTRY",
    passage: `Preach the Word, be ready in season and out of season! 2 Timothy 4:2`,
    description:
      "The Ministers' Ministry is a sacred fellowship of servant-leaders called to shepherd, teach, and uphold the spiritual health of the church and community. It exists to support, strengthen, and steward those who carry the mantle of pastoral leadership.",
    content:
      '<p>The Ministers\' Ministry is a sacred fellowship of servant-leaders called to shepherd, teach, and uphold the spiritual health of our church and community. Rooted in the biblical charge to "equip the saints for the work of ministry" (Ephesians 4:12), this ministry exists to support, strengthen, and steward those who carry the mantle of pastoral leadership.</p>',
    leaders: 'Rev. Kenny Davis (Ministers of the Word)',
    media: [
      'https://nmzlakeland.org/static/media/ministers.d56c74359c8b3b5b5c56.jpg',
    ],
  },
  {
    id: 'minister_wives_ministry',
    name: 'MINISTER WIVES MINISTRY',
    description:
      'The Minister Wives Ministry is a sacred sisterhood—a place of refuge, renewal, and rejoicing for women who walk faithfully beside their husbands in ministry. This ministry exists to uplift, equip, and celebrate them as they live out their own divine calling.',
    content:
      '<p>The Minister Wives Ministry is a sacred sisterhood—a place of refuge, renewal, and rejoicing for women who walk faithfully beside their husbands in ministry. These women carry unseen burdens, offer quiet strength, and serve with grace in the spotlight and behind the scenes. This ministry exists to uplift, equip, and celebrate them as they live out their own divine calling.</p>',
    leaders: null,
    media: [
      'https://nmzlakeland.org/static/media/ministers-wives.c0da1c3987fbd87633fa.jpg',
    ],
  },
  {
    id: 'nursing_home',
    name: 'NURSING HOME MINISTRY',
    description:
      "The goal of the Nursing Home Ministry is to glorify God by fellowshipping with the residents and staff of adult care facilities to share the love of our Lord, the Word of God, and the offer of salvation. The caring team shares God's Word, sings songs, and offers encouragement, hope, and prayer to the elderly.",
    content:
      "<p>The goal of the Nursing Home Ministry is to glorify God by fellowshipping with the residents and staff of adult care facilities to share the love of our Lord, the Word of God, and the offer of salvation through Jesus Christ our Lord. Our caring team of volunteers experience great joy as they share God's Word, sing songs of praise, and offer words of encouragement, hope, and prayer to the elderly who are living in the twilight years of their life. Our ministry team finds the time to talk, read, and sing to the residents while developing friendships through time spent ministering and sharing with them.</p>",
    leaders: 'Sister Gwendolyn Sanders',
    media: ['nursing.jpg'],
  },
  {
    id: 'music',
    name: 'MUSIC MINISTRY',
    passage: `“Let the message of Christ dwell among you richly, as you teach and admonish
one another with all wisdom through psalms, hymns, and songs from the Spirit,
singing to God with gratitude in your hearts.” Col. 3:16`,
    description:
      'The Church Choirs are a living testimony of praise—a collective voice raised to glorify God, edify the congregation, and embody the spirit of unity. The choir serves as both a musical leader and a spiritual witness. Every member should be a born again believer and faithful in attendance for regular rehearsals.',
    content:
      '<p>The Church Choirs are a living testimony of praise—a collective voice raised to glorify God, edify the congregation, and embody the spirit of unity. Rooted in biblical tradition and enriched by generations of faithful singers, the choir serves as both a musical leader and a spiritual witness. Every member should be a born again believer, and is expected to be committed and faithful in attendance for regular rehearsals.</p>',
    leaders: 'Bro Shawn Brown',
    media: [
      'https://nmzlakeland.org/static/media/music2.4151f91948fb6648cc15.png',
    ],
  },
  {
    id: 'musicians',
    name: 'MUSICIANS AND DIRECTORS STAFF',
    passage: `Let the message of Christ dwell among you richly as you teach and admonish one
another with all wisdom through psalms, hymns, and songs from the Spirit, singing to God with gratitude in your hearts. Colossians 3:16`,
    description:
      'The Musicians and Directors provide oversight of the choirs, praise and worship teams, and musicians. They assist the pastor in planning, conducting, and evaluating a comprehensive music ministry, and foster musical talents among all age groups.',
    content:
      "<p>The New Mt. Zion Musicians and Directors provide oversight of the choirs, praise and worship teams, and musicians. The pastor appoints the leaders of this ministry to direct music throughout the church's life. These leaders work collaboratively to assist the pastor in planning, conducting, and evaluating a comprehensive music ministry. This also includes using the congregation's musical gifts to aid praise and worship by leading the congregation to minister to each other through music. These musical leaders share an appreciation of music and foster musical talents and expression among all age groups within the church.</p>",
    leaders: 'Director of Ensembles/Choirs: Bro Armel Light',
    media: [
      'https://nmzlakeland.org/static/media/music.7bdf9fe26d8a7a3daaf7.jpg',
    ],
  },
  {
    id: 'dance',
    name: 'ANGELS DANCE MINISTRY',
    description:
      'The Dance Ministry exists to glorify God through the art of movement, offering a visual expression of worship that uplifts, inspires, and invites the congregation into deeper communion with the Holy Spirit. The dancers embody praise, intercession, and celebration.',
    content:
      '<p>The Dance Ministry exists to glorify God through the art of movement, offering a visual expression of worship that uplifts, inspires, and invites the congregation into deeper communion with the Holy Spirit. Rooted in Scripture and guided by reverence, our dancers embody praise, intercession, and celebration.</p>',
    leaders: 'Tiffani Simmons (NMZ Angels Dance Ministry)',
    media: [
      'https://nmzlakeland.org/static/media/angels.7c452fa5803c5d2c0f2f.jpg',
    ],
  },
  {
    id: 'parking',
    name: 'PARKING LOT MINISTRY',
    description:
      'The Parking Lot Ministry plays a vital role in ensuring a safe, welcoming, and well-organized arrival experience for all who attend worship services. Members assist with traffic flow and parking, helping guests and members navigate the grounds with ease.',
    content:
      '<p>The Parking Lot Ministry at New Mt. Zion plays a vital role in ensuring a safe, welcoming, and well-organized arrival experience for all who attend worship services. With a focus on both safety and hospitality, ministry members assist with traffic flow and parking, helping guests and members navigate the grounds with ease.</p>',
    leaders: 'Bro. Al Cooper',
    media: [
      'https://nmzlakeland.org/static/media/parking.4955679caf33a0cce807.jpg',
    ],
  },
  {
    id: 'security',
    name: 'SECURITY MINISTRY',
    passage: `Put on the full armor of God so that you can take your stand against the devil’s
schemes. Ephesians 6:11 NIV`,
    description:
      'This ministry is committed to ensuring a safe and secure worship environment, so every member and guest can gather in peace. Their comprehensive safety program is designed to respond effectively to a range of potential threats, including severe weather, disruptive behavior, and the protection of children. Volunteers must be active church members in good standing, age 21 and older.',
    content:
      '<p>At New Mt. Zion, creating a safe and secure worship environment is a sacred priority. We are committed to ensuring that every member and guest can gather in peace, knowing that thoughtful and proactive measures are in place to protect their well-being. Our comprehensive safety and security program is designed to respond effectively to a range of potential threats—including severe weather, disruptive behavior, property concerns, and the protection of children. These protocols reflect our deep responsibility to safeguard both the physical and spiritual atmosphere of our church. The Safety and Security Ministry is staffed by dedicated volunteers—active church members in good standing, age 21 and older—who feel called to serve in this vital role.</p>',
    leaders: 'Bro. Bruce Black',
    media: [
      'https://nmzlakeland.org/static/media/security.2b6f316299082015fec7.png',
    ],
  },
  {
    id: 'prayer',
    name: 'PRAYER MINISTRY',
    description:
      'The Prayer Ministry serves as a vital foundation for the spiritual life of the church. Faithful members gather before services to offer corporate prayer, interceding on behalf of the pastors, leaders, and congregation, creating a sacred covering.',
    content:
      "<p>The Prayer Ministry serves as a vital foundation for the spiritual life of the church. Faithful members gather before services to offer corporate prayer, interceding on behalf of our pastors, leaders, and congregation. Their presence creates a sacred covering, inviting God's guidance, strength, and peace into every aspect of our worship and ministry.</p>",
    leaders: 'Deacon RO Randall',
    media: [
      'https://nmzlakeland.org/static/media/music2.4151f91948fb6648cc15.png',
    ],
  },
  {
    id: 'van',
    name: 'VAN MINISTRY',
    description:
      'The Church Van Ministry is a lifeline of connection, care, and inclusion, providing rides to worship services, Sunday school, and church-wide events for members who might otherwise be unable to attend—especially seniors, youth, and families without reliable transportation. Drivers must be between 25 and 70 years old with five years of driving experience.',
    content:
      "<p>The Church Van Ministry is such a beautiful embodiment of service—it's more than just transportation; it's a lifeline of connection, care, and inclusion. The Ministry typically provides rides to worship services, Sunday school, and church-wide events for members who might otherwise be unable to attend—especially seniors, youth, and families without reliable transportation. Church van drivers must be at least 25 years, no more than 70 years old, and have five years of driving experience.</p>",
    leaders: 'Bro Fernando Smith (Transportation Ministry)',
    media: [
      'https://nmzlakeland.org/static/media/Van-ministry.f62a7f0896f58f762a08.jpg',
    ],
  },
  {
    id: 'youth',
    name: "YOUTH AND CHILDREN'S MINISTRY",
    passage: `“But Jesus called the children to Him and said, Let the little children come to me,
and do not hinder them, for the Kingdom of God belongs to such as these.”
LUKE 18-16 NIV`,
    description:
      'This ministry fosters an environment where every child (4 to 12 years) and teen (12 to 17 years) can develop a personal relationship with Jesus Christ and grow to love and serve Him through worship and teaching the Bible. It ensures spiritual growth happens in an atmosphere of physical and emotional safety. All volunteers must complete an application and background check.',
    content: `<p>
      The New Mt. Zion Missionary Baptist Church’s Youth and Children’s
Ministry fosters an environment where every child can develop a
personal relationship with Jesus Christ and grow to love and serve
Him through worship and teaching the Bible. The youth ministry
ensures that spiritual growth happens in an atmosphere of physical
and emotional safety.
      </p>
      <p>
      The ministry strives to create a supportive environment that is
healthy, edifying, and loving. The church ensures that all youth
volunteers act according to church policies and God’s higher stand-
ards of conduct. The youth ministry welcomes children from 4
years to 12 years and teens from 12 years to 17 years of age. All
youth ministry volunteers must complete an application and back-
ground check.
      </p>`,
    leaders: 'Sis Natalie Montgomery',
    media: [
      'https://nmzlakeland.org/static/media/youth.b4dc53ae316ac31481b8.jpg',
    ],
  },
  {
    id: 'senior_women',
    name: "SENIOR WOMEN'S MINISTRY",
    description:
      'This ministry honors the spiritual maturity and life experience of older women, fosters mentorship and intergenerational discipleship, and empowers service through prayer teams, hospitality, outreach, and legacy projects.',
    content:
      "<p>The Senior Women's Ministry is a radiant thread in the fabric of church life—woven with wisdom, grace, and generational strength. Senior women's ministries serve to honor the spiritual maturity and life experience of older women, foster mentorship and intergenerational discipleship (Titus 2:3-5) and empower service through prayer teams, hospitality, outreach, and legacy projects.</p>",
    leaders: 'Sandra Warner',
    media: [
      'https://nmzlakeland.org/static/media/senior_women.5cafae7fa7ab4eb43d18.jpg',
    ],
  },
  {
    id: 'sis',
    name: 'SISTERS IN SPIRIT (SIS) MINISTRY',
    passage: `“She is clothed with strength and dignity; she can laugh at the days
to come.” — Proverbs 31:25`,
    description:
      "The Young Women's Ministry is a sacred space where faith meets friendship, and purpose is nurtured through connection, creativity, and Christ-centered growth. Its mission is to cultivate a community of young women who are spiritually grounded, emotionally supported, and equipped to lead with grace, courage, and conviction.",
    content:
      "<p>The Young Women's Ministry is a sacred space where faith meets friendship, and purpose is nurtured through connection, creativity, and Christ-centered growth. We gather to uplift one another, explore our callings, and build a legacy of leadership, love, and service. Our mission is to cultivate a community of young women who are spiritually grounded, emotionally supported, and equipped to lead with grace, courage, and conviction.</p>",
    leaders: 'Nicole Link, Rahsheia Quary',
    media: [
      'https://nmzlakeland.org/static/media/sis1.db3c59beb263740d6b78.jpeg',
    ],
  },
  {
    id: 'health',
    name: 'HEALTH AND WELLNESS MINISTRY',
    passage: `Do you not know that your bodies are temples of the Holy Spirit, who is in you,
whom you have received from God? You are not your own; 20 you were bought
at a price. Therefore, honor God with your bodies. 1 Corinthians 6: 10-20 NIV`,
    description:
      'The Health and Wellness Ministry exists to uplift, educate, and empower members and neighbors to live well, honoring the sacred gift of life. A primary goal is to develop educational programs to move faith from health awareness to health engagement. Its principles include Faith and Prayer, Education and Empowerment, Advocacy and Support, and Health and well-being.',
    content:
      "<p>At the heart of our congregation is a commitment to holistic care—body, mind, and spirit. The Health and Wellness Ministry exists to uplift, educate, and empower our members and neighbors to live well, honoring the sacred gift of life God has entrusted to us. A primary goal is to develop educational programs to move our faith from health awareness to health engagement. The ministry's core values and attributes are based on Biblical guidance. The founding principles include Faith and Prayer, Education and Empowerment, Advocacy and Support, and Health and well-being.</p>",
    leaders: 'Sis. Barbara Harrison',
    media: [
      'https://nmzlakeland.org/static/media/health.98a6f196b6309c5659ae.jpg',
    ],
  },
  {
    id: 'administrative_staff',
    name: 'ADMINISTRATIVE STAFF',
    passage: `“Galatians 5:22-23 “But the fruit of the Spirit is love, joy, peace, pa-
tience, kindness, goodness, faithfulness, gentleness and self-
control.” The fruit of the Holy Spirit is the result of the Holy Spirit’s
presence in the life of a Christian.`,
    description:
      'The Administrative staff are trusted gatekeepers who meet and greet parishioners and visitors electronically, by phone, and in person. They ensure the church is well-run and maintained by managing daily operations, supervising staff, keeping parishioners informed, managing the calendar, keeping accurate financial records, and supporting the pastor.',
    content:
      "<p>The Administrative staff of NMZ are trusted gatekeepers who bear humility, a fruit of the Holy Spirit and as such reflects the nature of God in his/her behavior. The staff meet and greet parishioners and visitors electronically, by phone, and in person. The Administrative staff ensures the church is well-run and maintained by managing its daily operations through supervising staff and volunteers, keeping parishioners informed, and managing the church's calendar and events. The staff ensures the effective use of church income by keeping accurate records and work with the community and supports the pastor and other church staff members.</p>",
    leaders:
      'Office Ministry Leader: Sis. Beverly Boatwright. Church Secretary: Sis. Emma Brooks. Financial Secretaries: Sis. Jessie Gregory. Church Business Manager: Sis Barbara Harrison. Church Clerk: Sis. Velma Pearson. Assistant Church Clerk: Sis. Jackie Davis.',
    media: [
      'https://nmzlakeland.org/static/media/office.8fe0d1c4997d7a482046.jpg',
    ],
  },
  {
    id: 'history',
    name: 'HISTORY AND HERITAGE COMMITTEE',
    description:
      'The Committee documents the historical journey of New Mt. Zion with orderly records so that generational heritage, discipleship, and special events are preserved. It involves locating and preserving historical records and photos, assisting in keeping accurate current records, and helping with special occasions like the church anniversary.',
    content:
      "<p>The History and Heritage Committee documents the historical journey of New Mt. Zion with orderly records so that our generational heritage, discipleship, and special events are documented and preserved. We seek to honor our past, rejoice in the present, and prayerfully shape our future through our recorded activities. This ministry's role includes: <ul><li>Locating and preserving all the church's historical records, photographs, and other related materials</li><li>Assist the church in making and keeping accurate, comprehensive records of its current life and work.</li><li>Assist the church with special occasions such as the church anniversary.</li><li>Communicate our heritage and the generation grace of the membership</li></ul></p>",
    leaders: 'Sis Keitha Salmon (Historian Committee)',
    media: ['heritage.jpg'],
  },
  {
    id: 'new_member',
    name: 'NEW MEMBER MINISTRY',
    passage: `" So, in Christ, we, though many, form one body, and each member belongs
to all the others. " Romans 12:5 NIV`,
    description:
      "With the guidance of the Holy Spirit, the New Member Ministry's purpose is to welcome and connect newly joined members into a joyful fellowship with Christ and the church family. New member orientation classes provide the essential introduction to the church's mission, ordinances, and commitments to Christ. All new members must complete four consecutive weekly classes.",
    content:
      "<p>With the guidance of the Holy Spirit, the New Member Ministry's purpose is to welcome and connect newly joined members into a joyful fellowship with our Savior, Jesus Christ, and the New Mt. Zion's church family. We are a Bible-based church focusing on relevant teaching, heart-felt worship, honest friendships, constant prayer, and compassionate care for those in need. Our goal is to create an environment of contagious Christianity that influences and encourages the members. The new member orientation classes provide the essential introduction to the church's mission, ordinances, and, most importantly, our commitments to Christ and His Church. All new members must complete four consecutive weekly classes starting on the first Sunday of each month beginning at 9:30 AM.</p>",
    leaders:
      'Deacon Walter Brown, Bro. Arnie Travis, Clerk Sis Velma Pearson, Bro Michal Davis, Sis Jackie Davis',
    media: [
      'https://nmzlakeland.org/static/media/newmember.803f575f170e24f328e6.jpg',
    ],
  },
  {
    id: 'men',
    name: "MEN'S MINISTRY",
    passage: `Proverbs 27:17 "Iron sharpens iron, and one man sharpens another."
This verse emphasizes the importance of mutual support and constructive
relationships, suggesting that individuals can help each other grow and improve
through interaction and challenge.`,
    description:
      "The men's ministry focuses on the spiritual, emotional, and social needs of men. It aims to provide a space for men to grow in their faith, build strong relationships, and engage in community service, often including Bible study, prayer, small groups, and mission trips.",
    content:
      "<p>The church men's ministry focuses on the spiritual, emotional, and social needs of men. It aims to provide a space for men to grow in their faith, build strong relationships, and engage in community service. The ministry often includes activities such as Bible study, prayer, small groups, and mission trips, fostering a sense of community and shared purpose among men. It is a vital component of a church's mission, serving as a platform for spiritual growth, leadership development, and service to the community.</p>",
    leaders: 'Bro Jeff Stevenson',
    media: [
      'https://nmzlakeland.org/static/media/men.b3ec715f8333647e0247.jpg',
    ],
  },
  {
    id: 'apologetic',
    name: 'APOLOGETIC MINISTRY',
    description:
      "The Apologetic Ministry focuses on defending and explaining Christian beliefs through reason, evidence, and respectful dialogue. It equips believers to respond thoughtfully to questions and challenges about Christianity and engages skeptics with reasoned discussion for the existence of God, the reliability of Scripture, and the truth of Jesus' resurrection.",
    content:
      "<p>The Apologetic Ministry focuses on defending and explaining Christian beliefs through reason, evidence, and respectful dialogue. The Ministry equips believers to respond thoughtfully to questions and challenges about Christianity. They also engage skeptics with reasoned discussion for the existence of God, the reliability of Scripture, and the truth of Jesus' resurrection.</p>",
    leaders: 'Bro Marius Link',
    media: [
      'https://nmzlakeland.org/static/media/apologetic.640e51c2a926f46295ab.jpg',
    ],
  },
  {
    id: 'fellowship',
    name: 'FELLOWSHIP ACTIVITIES MINISTRY',
    description:
      'The Fellowship Activities Ministry is a vibrant heartbeat of the church, designed to foster fellowship, fun, and faith through engaging events and gatherings. It is often the bridge that connects generations, welcomes newcomers, and strengthens bonds within the congregation.',
    content:
      "<p>The Fellowship Activities Ministry is a vibrant heartbeat of the church, designed to foster fellowship, fun, and faith through engaging events and gatherings. It's often the bridge that connects generations, welcomes newcomers, and strengthens bonds within the congregation.</p>",
    leaders: 'Sis Cora Rankins',
    media: [
      'https://nmzlakeland.org/static/media/hosp.489e0996fbe19cbcdffc.jpg',
    ],
  },
  {
    id: 'photography',
    name: 'PHOTOGRAPHY MINISTRY',
    description:
      'The Photography Ministry captures moments of faith and serving in the church and in the community. It documents church life from baptisms and worship services to outreach events, preserving the heartbeat of the ministry as visual archives that honor leaders, milestones, and community impact.',
    content:
      '<p>The Photography Ministry captures moments of faith and serving in the church and in the community. In snapping pictures, it documents church life from baptisms and worship services to outreach events and galas, photographers that preserve the heartbeat of the ministry. Photos become visual archives, honoring present and past leaders, milestones, and community impact.</p>',
    leaders: 'Bro Kenny Williams',
    media: [
      'https://nmzlakeland.org/static/media/photo.006b05ad71add0dfadba.jpg',
    ],
  },
  {
    id: 'baptism_communion',
    name: 'BAPTISM & COMMUNION MINISTRY',
    description:
      'This ministry is one of the most sacred and spiritually rich expressions of church life. It involves shepherding souls through defining moments and guiding individuals through the public declaration of their faith, symbolizing rebirth, cleansing, and unity with Christ.',
    content:
      "<p>The Baptism and Communion Ministry is one of the most sacred and spiritually rich expressions of church life. It's not just about administering sacraments; it's about shepherding souls through defining moments and guiding individuals through the public declaration of their faith, symbolizing rebirth, cleansing, and unity with Christ.</p>",
    leaders: 'Deacon Eugene Byron',
    media: [
      'https://nmzlakeland.org/static/media/deacons.8ce8c86612d9b6df0b0c.jpg',
    ],
  },
  {
    id: 'av',
    name: 'AUDIO-VISUAL MINISTRY',
    description:
      'The Audio-Visual Ministry Team meets the overall audio-visual needs and is responsible for providing a distraction-free worship experience. This team develops, supports, and enhances the technical aspects of services, is accountable for soundboard management and equipment, and ensures live streaming and video production are performed with excellence.',
    content:
      '<p>The Audio-Visual Ministry Team meets the overall audio-visual needs of New Mt. Zion Missionary Baptist Church and is responsible for providing a distraction-free audio-visual worship experience. This team develops, supports, and enhances the technical aspects of the worship services and is responsible for the quality of the audio generated during each church service. The audio-visual team is accountable for soundboard management and equipment and ensures that event live streaming and video management production is performed with excellence. This team is comprised of interdisciplinary cyber evangelists who possess expertise in the mediums of photography, video, audio, social media, online streaming, and other technologies.</p>',
    leaders: 'Kendric Owens, Erik Bland, Ruky Ojameruaye, Marcus Barnes',
    media: ['https://nmzlakeland.org/static/media/av.5a5719a141ccc315d30a.png'],
  },
  {
    id: 'clothes_closet',
    name: 'CLOTHES CLOSET MINISTRY',
    description:
      'A beautiful expression of compassion in action, this ministry provides gently used clothing to individuals and families in need, free of charge and free of judgment. It is open on Mondays and Thursdays 10 am-1 PM, and accepts donations of clean and usable clothing and small household items.',
    content:
      "<p>A church clothes closet is a beautiful expression of compassion in action—a ministry that provides gently used clothing to individuals and families in need, free of charge and free of judgment. It's more than racks of garments; it's a place where dignity is restored, burdens are lightened, and community is woven together one outfit at a time. The closet is open on Mondays and Thursdays 10 am-1 PM. And accept donations of clean and usable clothing and small household items.</p>",
    leaders: null,
    media: [
      'https://nmzlakeland.org/static/media/closet.9c6b2bc85824ebca494d.png',
    ],
  },
  {
    id: 'culinary',
    name: 'CULINARY MINISTRY',
    description:
      'The Culinary Ministry provides meals for church events, funerals, repasts, receptions, and outreach programs. It offers a beautiful expression of service, fellowship, and care by creating welcoming spaces where people connect over food.',
    content:
      '<p>The Culinary Ministry provides meals for church events, funerals, repasts, receptions, and outreach programs. It provides a beautiful expression of service, fellowship and care by creating welcoming spaces where people connect over food, deepening relationships across generations.</p>',
    leaders: 'Sis. May Bennett',
    media: [
      'https://nmzlakeland.org/static/media/culinary.e525c0f4e2b0a2d4e284.jpg',
    ],
  },
  {
    id: 'praise_team',
    name: 'Praise Team',
    description:
      'This ministry is dedicated to leading the congregation in worship through song. (Summary inferred from ministry name and context.)',
    content:
      '<p>This ministry is dedicated to leading the congregation in worship through song and is overseen by the Music Ministry staff</p>',
    leaders: 'Bro Deon Goodman',
    media: [
      'https://nmzlakeland.org/static/media/music2.4151f91948fb6648cc15.png',
    ],
  },
];

export const submitContactRequest = ({
  firstName,
  lastName,
  email,
  phone,
  note,
  selectedOption,
}: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  selectedOption: string;
}) => {
  return addDoc(collection(db, 'forms'), {
    firstName,
    lastName,
    email,
    phone,
    note,
    selectedOption,
    formId: FormIds.contact,
  });
};

export const submitVolunteerForm = (data: VolunteerInput) => {
  return addDoc(collection(db, 'forms'), {
    ...data,
    formId: FormIds.volunteer,
  });
};
export const submitBudgetForm = (data: IBudgetFormData) => {
  return addDoc(collection(db, 'forms'), {
    ...data,
    formId: FormIds.budgetForm,
    year: new Date().getFullYear(),
    createdOn: serverTimestamp(),
  });
};

export const submitBreastForm = (data: IBreastFormData) => {
  return addDoc(collection(db, 'forms'), {
    ...data,
    formId: FormIds.breastCancerSeminar,
    year: new Date().getFullYear(),
    createdOn: serverTimestamp(),
  });
};

export const submitForm = (
  formId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, string | number | Date | any>
) => {
  //console.log({ formId, data });
  return addDoc(collection(db, 'forms'), {
    ...data,
    formId,
    createdOn: new Date(),
  });
};

export const useEvents = (id = '') => {
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState<ChurchEvent[]>([]);
  useEffect(() => {
    setEvents([]);
    const q = !id
      ? query(collection(db, 'events'), orderBy('createdOn', 'desc'), limit(50))
      : query(collection(db, 'events'), where('id', '==', id), limit(50));
    //04/24/2024
    setBusy(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setBusy(false);
      const z: ChurchEvent[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        z.push({
          ...data,
          ...{
            id: doc.id,
            startTime: data.startTime.toDate(),
            endTime: data.endTime.toDate(),
          },
        } as ChurchEvent);
      });

      setEvents(z);
    });
    return () => {
      unsubscribe();
    };
  }, [id]);

  const createEvent = async (data: ChurchEvent) => {
    const storage = getStorage();
    const mediaUrls = await Promise.all(
      data.media.map(async (media) => {
        if (media.startsWith('data:image')) {
          const storageRef = ref(
            storage,
            `events/${Date.now()}-${Math.random().toString(36).substring(7)}`
          );
          await uploadString(storageRef, media, 'data_url');
          return await getDownloadURL(storageRef);
        }
        return media;
      })
    );

    const nref = doc(collection(db, 'events'));
    return setDoc(nref, {
      ...data,
      media: mediaUrls,
      createdOn: new Date(),
    });
  };

  const deleteEvent = (id: string) => {
    return deleteDoc(doc(collection(db, 'events'), id));
  };

  const updateEvent = (id: string, data: Partial<ChurchEvent>) => {
    return updateDoc(doc(collection(db, 'events'), id), data);
  };
  return { busy, events, createEvent, deleteEvent, updateEvent };
};

export interface Bulletin {
  id: string;
  link: string;
  month: string;
  year: string;
  createdOn: Date;
}
export const useBulletins = (id = '') => {
  const [busy, setBusy] = useState(false);
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  useEffect(() => {
    setBulletins([]);
    const q = !id
      ? query(
          collection(db, 'bulletins'),
          orderBy('createdOn', 'desc'),
          limit(50)
        )
      : query(collection(db, 'bulletins'), where('id', '==', id), limit(50));
    //04/24/2024
    setBusy(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setBusy(false);
      const z: Bulletin[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        z.push({
          ...data,
          ...{
            id: doc.id,
            createdOn: data.createdOn.toDate(),
          },
        } as Bulletin);
      });

      setBulletins(z);
    });
    return () => {
      unsubscribe();
    };
  }, [id]);

  const createBulletin = async ({
    month,
    year,
    file,
  }: {
    month: string;
    year: string;
    file: File | null;
  }) => {
    const data = {
      id: `${month}-${year}`,
      month,
      year,
      link: '',
      createdOn: new Date(),
    };
    const storage = getStorage();

    if (file !== null) {
      const storageRef = ref(
        storage,
        `bulletins/${Date.now()}-${Math.random().toString(36).substring(7)}`
      );
      await uploadBytesResumable(storageRef, file);
      data.link = await getDownloadURL(storageRef);
    }
    const nref = doc(collection(db, 'bulletins'), data.id);
    return setDoc(nref, data);
  };

  const deleteBulletin = (id: string) => {
    return deleteDoc(doc(collection(db, 'bulletins'), id));
  };

  return { busy, bulletins, createBulletin, deleteBulletin };
};

export const createNewAnnouncement = (data: Announcement) => {
  const nref = doc(collection(db, 'announcements'));
  return setDoc(nref, {
    ...data,
    id: nref.id,
  });
};

export const deleteAnnouncement = (id: string) => {
  return deleteDoc(doc(collection(db, 'announcements'), id));
};
export const useAnnouncements = (all = false) => {
  const [busy, setBusy] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  useEffect(() => {
    setAnnouncements([]);
    const q = all
      ? query(
          collection(db, 'announcements'),
          orderBy('createdOn', 'desc'),
          limit(50)
        )
      : query(
          collection(db, 'announcements'),
          where('displayUntil', '>=', new Date()),
          where('enabled', '==', true),
          limit(50)
        );
    //04/24/2024
    setBusy(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setBusy(false);
      const z: Announcement[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        z.push({
          ...data,
          ...{
            id: doc.id,
            createdOn: data.createdOn.toDate(),
            displayUntil: data.displayUntil.toDate(),
          },
        } as Announcement);
      });

      setAnnouncements(z);
    });
    return () => {
      unsubscribe();
    };
  }, [all]);

  return { busy, announcements };
};

export const getCompletedForm = async (id: string) => {
  const docRef = doc(db, 'forms', id);

  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    return null;
  }
};

export const shortenlUrl = async (link: string) => {
  const longUrl = encodeURIComponent(link);
  const url = `https://tinyurl.com/api-create.php?url=${longUrl}`;

  fetch(url)
    .then((response) => response.text())
    .then((data) => console.log(data))
    .catch((error) => console.error('Error:', error));
};

// Polls

export interface Poll {
  id: string;
  question: string;
  options: string[];
  createdOn: Date;
  creatorId: string;
  active: boolean;
  multipleChoice?: boolean;
}

export interface PollVote {
  optionIndex?: number;
  optionIndices?: number[];
  votedAt: Date;
}

export const usePolls = () => {
  const [busy, setBusy] = useState(false);
  const [polls, setPolls] = useState<Poll[]>([]);
  useEffect(() => {
    setPolls([]);
    const q = query(collection(db, 'polls'));
    setBusy(true);
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        setBusy(false);
        const z: Poll[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          z.push({
            ...data,
            id: doc.id,
            createdOn: data.createdOn?.toDate?.() || new Date(),
          } as Poll);
        });
        z.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
        setPolls(z);
      },
      (error) => {
        console.error('Error listening to polls:', error);
        setBusy(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const createPoll = async (data: Omit<Poll, 'id'>) => {
    const nref = doc(collection(db, 'polls'));
    return setDoc(nref, {
      ...data,
      id: nref.id,
      createdOn: new Date(),
    });
  };

  const deletePoll = (id: string) => {
    return deleteDoc(doc(collection(db, 'polls'), id));
  };

  const togglePollActive = (id: string, active: boolean) => {
    return updateDoc(doc(collection(db, 'polls'), id), { active });
  };

  return { busy, polls, createPoll, deletePoll, togglePollActive };
};

export const usePollVotes = (pollId: string) => {
  const [votes, setVotes] = useState<PollVote[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!pollId) return;
    setVotes([]);
    const votesRef = collection(db, 'polls', pollId, 'votes');
    setBusy(true);
    const unsubscribe = onSnapshot(votesRef, (querySnapshot) => {
      setBusy(false);
      const z: PollVote[] = [];
      querySnapshot.forEach((d) => {
        const data = d.data();
        z.push({
          optionIndex: data.optionIndex,
          optionIndices: data.optionIndices,
          votedAt: data.votedAt?.toDate?.() || new Date(),
        });
      });
      setVotes(z);
    });
    return () => unsubscribe();
  }, [pollId]);

  return { busy, votes };
};

const getVoterId = (): string => {
  const key = 'nmz_voter_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

export const submitVote = async (
  pollId: string,
  selection: number | number[]
) => {
  const voterId = getVoterId();
  const voteRef = doc(db, 'polls', pollId, 'votes', voterId);
  if (Array.isArray(selection)) {
    return setDoc(voteRef, {
      optionIndices: selection,
      votedAt: new Date(),
    });
  }
  return setDoc(voteRef, {
    optionIndex: selection,
    votedAt: new Date(),
  });
};

export const hasVoted = (pollId: string): boolean => {
  const voterId = getVoterId();
  return !!localStorage.getItem(`nmz_voted_${pollId}_${voterId}`);
};

export const markVoted = (pollId: string): void => {
  const voterId = getVoterId();
  localStorage.setItem(`nmz_voted_${pollId}_${voterId}`, 'true');
};

export const getPoll = async (id: string) => {
  const docRef = doc(db, 'polls', id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdOn: data.createdOn?.toDate?.() || new Date(),
    } as Poll;
  }
  return null;
};

// Products

export interface Product {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  orderLink: string;
  active: boolean;
}

export const useProducts = () => {
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    setProducts([]);
    const q = query(collection(db, 'products'), orderBy('name', 'asc'));
    setBusy(true);
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        setBusy(false);
        const z: Product[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          z.push({
            id: doc.id,
            name: data.name || '',
            description: data.description || '',
            imageUrl: data.imageUrl || '',
            price: typeof data.price === 'number' ? data.price : 0,
            orderLink: data.orderLink || '',
            active: data.active || false,
          });
        });
        setProducts(z.filter((x) => x.active));
      },
      () => {
        setBusy(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return { busy, products };
};

export const createProduct = (data: Omit<Product, 'id'>) => {
  const nref = doc(collection(db, 'products'));
  return setDoc(nref, {
    ...data,
    id: nref.id,
  });
};

export const deleteProduct = (id: string) => {
  return deleteDoc(doc(collection(db, 'products'), id));
};
